import { useUnit } from 'effector-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { closePositionFx, placesFor, type Position } from '../../entities/position';
import { fromLocalInput, toLocalInput } from '../../shared/lib/dates';
import { parseDecimal } from '../../shared/lib/decimal';
import { formatMoney, formatPercent, formatShares, toNumber } from '../../shared/lib/format';
import { useLocale } from '../../shared/lib/i18n/use-locale';
import { Button, Dialog, DialogContent, Field, Input, PlValue } from '../../shared/ui';
import { $errors, $position, $preview, closeDialogClosed, closePreview } from './model';

export function ClosePositionDialog() {
  const [position, close] = useUnit([$position, closeDialogClosed]);
  return (
    <Dialog
      open={position !== null}
      onOpenChange={(open) => {
        if (!open) close();
      }}
    >
      {position && <CloseForm key={position.id} position={position} />}
    </Dialog>
  );
}

function CloseForm({ position }: { position: Position }) {
  const { t } = useTranslation();
  const locale = useLocale();
  const places = placesFor(position);
  const suggested = position.quote?.price ?? position.holding.target_price;
  const [price, setPrice] = useState(locale === 'bg' ? suggested.replace('.', ',') : suggested);
  const [executedAt, setExecutedAt] = useState(toLocalInput(new Date()));
  const [attempted, setAttempted] = useState(false);
  const [preview, serverErrors, pending, close, submit, requestPreview] = useUnit([
    $preview,
    $errors,
    closePositionFx.pending,
    closeDialogClosed,
    closePositionFx,
    closePreview.run,
  ]);
  const parsed = parseDecimal(price, locale);
  const parsedTime = fromLocalInput(executedAt);

  useEffect(() => {
    if (parsed === null || Number(parsed) <= 0) return undefined;
    const timer = window.setTimeout(() => {
      void requestPreview({ id: position.id, price: parsed }).catch(() => undefined);
    }, 150);
    return () => {
      window.clearTimeout(timer);
    };
  }, [parsed, position.id, requestPreview]);

  function onSubmit(): void {
    setAttempted(true);
    if (parsed === null || parsedTime === null) return;
    void submit({ id: position.id, body: { price: parsed, executed_at: parsedTime } }).catch(
      () => undefined,
    );
  }

  const priceError =
    serverErrors.price ??
    (price.trim() !== '' && parsed === null
      ? t('form.notANumber')
      : attempted && parsed === null
        ? t('form.required')
        : undefined);

  return (
    <DialogContent
      title={t('closePosition.title', { symbol: position.instrument.display_symbol })}
      description={t('closePosition.description')}
      closeLabel={t('common.close')}
      onSubmit={onSubmit}
      footer={
        <>
          <Button onClick={close}>{t('common.cancel')}</Button>
          <Button type="submit" variant="primary" disabled={pending}>
            {t('closePosition.submit')}
          </Button>
        </>
      }
    >
      <div className="space-y-2">
        <Field label={t('closePosition.price')} error={priceError}>
          {({ id, describedBy, invalid }) => (
            <Input
              id={id}
              inputMode="decimal"
              numeric
              unit="€"
              autoComplete="off"
              value={price}
              aria-describedby={describedBy}
              aria-invalid={invalid || undefined}
              onChange={(event) => {
                setPrice(event.target.value);
              }}
            />
          )}
        </Field>
        <Field label={t('closePosition.shares')}>
          {({ id }) => (
            <Input id={id} readOnly numeric value={formatShares(position.holding.shares, locale)} />
          )}
        </Field>
        <Field label={t('closePosition.executedAt')} error={serverErrors.executed_at}>
          {({ id, describedBy, invalid }) => (
            <Input
              id={id}
              type="datetime-local"
              value={executedAt}
              aria-describedby={describedBy}
              aria-invalid={invalid || undefined}
              onChange={(event) => {
                setExecutedAt(event.target.value);
              }}
            />
          )}
        </Field>
      </div>
      {preview && (
        <dl className="mt-4 grid grid-cols-2 gap-3 rounded-lg border border-border-subtle bg-bg-1 px-3 py-2.5 text-xs">
          <div>
            <dt className="text-text-3">{t('closePosition.proceeds')}</dt>
            <dd className="tabular mt-0.5 text-sm font-medium">
              {formatMoney(preview.proceeds, locale)}
            </dd>
          </div>
          <div>
            <dt className="text-text-3">{t('closePosition.result')}</dt>
            <dd className="mt-0.5 text-sm font-medium">
              <PlValue value={toNumber(preview.pl)}>
                {formatMoney(preview.pl, locale, { signed: true })} ·{' '}
                {formatPercent(preview.pl_pct, locale, { signed: true })}
              </PlValue>
            </dd>
          </div>
          <p className="col-span-2 text-text-3">
            {t('closePosition.target', {
              price: formatMoney(position.holding.target_price, locale, { places }),
            })}
          </p>
        </dl>
      )}
    </DialogContent>
  );
}
